import { isNumber } from 'lodash';
import {
  OrderActionDto,
  OrderDurationDto,
  OrderTypeDto,
  OrderStatusIntDto,
  OrderTypeIntDto,
  OrderActionIntDto,
  OrderDurationIntDto,
  OrderStatusDto,
} from 'src/dtos';

export enum OrderTypeLabel {
  Market = 'Market Order',
  Limit = 'Limit Order',
  StopMarket = 'Stop Market Order',
  StopLimit = 'Stop Limit Order',
  MarketOnClose = 'Market on Close Order',
}

export enum OrderActionLabel {
  Buy = 'Buy',
  Sell = 'Sell',
}

export enum OrderDurationLabel {
  Day = 'End of Day',
  GTC = 'Good Until Cancelled',
}

export enum OrderStatusLabel {
  WORKING = 'WORKING',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum OrderEventStatusLabel {
  Open = 'Open',
  PartiallyFilled = 'Partially Filled',
  Filled = 'Filled',
  DoneForDay = 'Done For Day',
  Cancelled = 'Cancelled',
  Replaced = 'Replaced',
  PendingCancel = 'Pending Cancel',
  Stopped = 'Stopped',
  Rejected = 'Rejected',
  Suspended = 'Suspended',
  PendingOpen = 'Pending Open',
  Calculated = 'Calculated',
  Expired = 'Expired',
  PendingReplace = 'Pending Replace',
  Saved = 'Saved',
  LiveUntriggered = 'Live Untriggered',
  Scheduled = 'Scheduled',
  OCOUntriggered = 'OCO Untriggered',
  CancelledUntriggered = 'Cancelled Untriggered',
  Queued = 'Queued',
  ReplaceInitiated = 'Replace Initiated',
  CancelInitiated = 'Cancel Initiated',
  CancelRejected = 'Cancel Rejected',
  ReplaceRejected = 'Replace Rejected',
  Busted = 'Busted',
  PreAllocated = 'Pre Allocated',
  Acknowledged = 'Acknowledged',
  PartiallyCancelled = 'Partially Cancelled',
}

export class OrderStatus {
  private _value: OrderStatusDto;
  private _label: OrderStatusLabel;
  private _eventLabel: OrderEventStatusLabel;

  constructor(status: OrderStatusDto) {
    this._value = status;
    this._label = this.findLabel();
    this._eventLabel = this.findEventLabel();
  }

  private findLabel(): OrderStatusLabel {
    switch (this._value) {
      case OrderStatusDto.Filled:
        return OrderStatusLabel.COMPLETED;
      case OrderStatusDto.Cancelled:
      case OrderStatusDto.PartiallyCancelled:
        return OrderStatusLabel.CANCELLED;
      case OrderStatusDto.Rejected:
      case OrderStatusDto.ReplaceRejected:
      case OrderStatusDto.CancelRejected:
      case OrderStatusDto.Suspended:
      case OrderStatusDto.Expired:
      case OrderStatusDto.Busted:
        return OrderStatusLabel.REJECTED;
      case OrderStatusDto.New:
      case OrderStatusDto.PartiallyFilled:
      case OrderStatusDto.PendingNew:
      case OrderStatusDto.PendingCancel:
      case OrderStatusDto.Scheduled:
      case OrderStatusDto.CancelInitiated:
      case OrderStatusDto.Stopped:
      case OrderStatusDto.Calculated:
      case OrderStatusDto.DoneForDay:
      case OrderStatusDto.Saved:
      case OrderStatusDto.Acknowledged:
      case OrderStatusDto.Replaced:
      case OrderStatusDto.PendingReplace:
      case OrderStatusDto.LiveUntriggered:
      case OrderStatusDto.OCO_Untriggered:
      case OrderStatusDto.CancelledUntriggered:
      case OrderStatusDto.Initiated:
      case OrderStatusDto.ReplaceInitiated:
      case OrderStatusDto.PreAllocated:
        return OrderStatusLabel.WORKING;
    }
  }

  private findEventLabel(): OrderEventStatusLabel {
    switch (this._value) {
      case OrderStatusDto.New:
        return OrderEventStatusLabel.Open;
      case OrderStatusDto.PartiallyFilled:
        return OrderEventStatusLabel.PartiallyFilled;
      case OrderStatusDto.Filled:
        return OrderEventStatusLabel.Filled;
      case OrderStatusDto.DoneForDay:
        return OrderEventStatusLabel.DoneForDay;
      case OrderStatusDto.Cancelled:
        return OrderEventStatusLabel.Cancelled;
      case OrderStatusDto.Replaced:
        return OrderEventStatusLabel.Replaced;
      case OrderStatusDto.PendingCancel:
        return OrderEventStatusLabel.PendingCancel;
      case OrderStatusDto.Stopped:
        return OrderEventStatusLabel.Stopped;
      case OrderStatusDto.Rejected:
        return OrderEventStatusLabel.Rejected;
      case OrderStatusDto.Suspended:
        return OrderEventStatusLabel.Suspended;
      case OrderStatusDto.PendingNew:
        return OrderEventStatusLabel.PendingOpen;
      case OrderStatusDto.Calculated:
        return OrderEventStatusLabel.Calculated;
      case OrderStatusDto.Expired:
        return OrderEventStatusLabel.Expired;
      case OrderStatusDto.PendingReplace:
        return OrderEventStatusLabel.PendingReplace;
      case OrderStatusDto.Saved:
        return OrderEventStatusLabel.Saved;
      case OrderStatusDto.LiveUntriggered:
        return OrderEventStatusLabel.LiveUntriggered;
      case OrderStatusDto.Scheduled:
        return OrderEventStatusLabel.Scheduled;
      case OrderStatusDto.OCO_Untriggered:
        return OrderEventStatusLabel.OCOUntriggered;
      case OrderStatusDto.CancelledUntriggered:
        return OrderEventStatusLabel.CancelledUntriggered;
      case OrderStatusDto.Initiated:
        return OrderEventStatusLabel.Queued;
      case OrderStatusDto.ReplaceInitiated:
        return OrderEventStatusLabel.ReplaceInitiated;
      case OrderStatusDto.CancelInitiated:
        return OrderEventStatusLabel.CancelInitiated;
      case OrderStatusDto.CancelRejected:
        return OrderEventStatusLabel.CancelRejected;
      case OrderStatusDto.ReplaceRejected:
        return OrderEventStatusLabel.ReplaceRejected;
      case OrderStatusDto.Busted:
        return OrderEventStatusLabel.Busted;
      case OrderStatusDto.PreAllocated:
        return OrderEventStatusLabel.PreAllocated;
      case OrderStatusDto.Acknowledged:
        return OrderEventStatusLabel.Acknowledged;
      case OrderStatusDto.PartiallyCancelled:
        return OrderEventStatusLabel.PartiallyCancelled;
    }
  }

  get value(): OrderStatusDto {
    return this._value;
  }

  get label(): OrderStatusLabel {
    return this._label;
  }

  get eventLabel(): OrderEventStatusLabel {
    return this._eventLabel;
  }

  get isWorking() {
    return this._label === OrderStatusLabel.WORKING;
  }

  get isCompleted() {
    return this._label === OrderStatusLabel.COMPLETED;
  }

  get isCancelled() {
    return this._label === OrderStatusLabel.CANCELLED;
  }

  get isRejected() {
    return this._label === OrderStatusLabel.REJECTED;
  }
}

export class OrderEventStatus {
  private _value: OrderStatusDto;
  private _label: OrderEventStatusLabel;

  constructor(status: OrderStatusIntDto) {
    this._value = this.mapStatusNumberToEnum(status);
    this._label = this.findLabel();
  }

  private mapStatusNumberToEnum(status: OrderStatusIntDto): OrderStatusDto {
    switch (status) {
      case OrderStatusIntDto.New:
        return OrderStatusDto.New;
      case OrderStatusIntDto.PartiallyFilled:
        return OrderStatusDto.PartiallyFilled;
      case OrderStatusIntDto.Filled:
        return OrderStatusDto.Filled;
      case OrderStatusIntDto.DoneForDay:
        return OrderStatusDto.DoneForDay;
      case OrderStatusIntDto.Cancelled:
        return OrderStatusDto.Cancelled;
      case OrderStatusIntDto.Replaced:
        return OrderStatusDto.Replaced;
      case OrderStatusIntDto.PendingCancel:
        return OrderStatusDto.PendingCancel;
      case OrderStatusIntDto.Stopped:
        return OrderStatusDto.Stopped;
      case OrderStatusIntDto.Rejected:
        return OrderStatusDto.Rejected;
      case OrderStatusIntDto.Suspended:
        return OrderStatusDto.Suspended;
      case OrderStatusIntDto.PendingNew:
        return OrderStatusDto.PendingNew;
      case OrderStatusIntDto.Calculated:
        return OrderStatusDto.Calculated;
      case OrderStatusIntDto.Expired:
        return OrderStatusDto.Expired;
      case OrderStatusIntDto.PendingReplace:
        return OrderStatusDto.PendingReplace;
      case OrderStatusIntDto.Saved:
        return OrderStatusDto.Saved;
      case OrderStatusIntDto.LiveUntriggered:
        return OrderStatusDto.LiveUntriggered;
      case OrderStatusIntDto.Scheduled:
        return OrderStatusDto.Scheduled;
      case OrderStatusIntDto.OCO_Untriggered:
        return OrderStatusDto.OCO_Untriggered;
      case OrderStatusIntDto.CancelledUntriggered:
        return OrderStatusDto.CancelledUntriggered;
      case OrderStatusIntDto.Initiated:
        return OrderStatusDto.Initiated;
      case OrderStatusIntDto.ReplaceInitiated:
        return OrderStatusDto.ReplaceInitiated;
      case OrderStatusIntDto.CancelInitiated:
        return OrderStatusDto.CancelInitiated;
      case OrderStatusIntDto.CancelRejected:
        return OrderStatusDto.CancelRejected;
      case OrderStatusIntDto.ReplaceRejected:
        return OrderStatusDto.ReplaceRejected;
      case OrderStatusIntDto.Busted:
        return OrderStatusDto.Busted;
      case OrderStatusIntDto.PreAllocated:
        return OrderStatusDto.PreAllocated;
      case OrderStatusIntDto.Acknowledged:
        return OrderStatusDto.Acknowledged;
      case OrderStatusIntDto.PartiallyCancelled:
        return OrderStatusDto.PartiallyCancelled;
    }
  }

  private findLabel(): OrderEventStatusLabel {
    switch (this._value) {
      case OrderStatusDto.New:
        return OrderEventStatusLabel.Open;
      case OrderStatusDto.PartiallyFilled:
        return OrderEventStatusLabel.PartiallyFilled;
      case OrderStatusDto.Filled:
        return OrderEventStatusLabel.Filled;
      case OrderStatusDto.DoneForDay:
        return OrderEventStatusLabel.DoneForDay;
      case OrderStatusDto.Cancelled:
        return OrderEventStatusLabel.Cancelled;
      case OrderStatusDto.Replaced:
        return OrderEventStatusLabel.Replaced;
      case OrderStatusDto.PendingCancel:
        return OrderEventStatusLabel.PendingCancel;
      case OrderStatusDto.Stopped:
        return OrderEventStatusLabel.Stopped;
      case OrderStatusDto.Rejected:
        return OrderEventStatusLabel.Rejected;
      case OrderStatusDto.Suspended:
        return OrderEventStatusLabel.Suspended;
      case OrderStatusDto.PendingNew:
        return OrderEventStatusLabel.PendingOpen;
      case OrderStatusDto.Calculated:
        return OrderEventStatusLabel.Calculated;
      case OrderStatusDto.Expired:
        return OrderEventStatusLabel.Expired;
      case OrderStatusDto.PendingReplace:
        return OrderEventStatusLabel.PendingReplace;
      case OrderStatusDto.Saved:
        return OrderEventStatusLabel.Saved;
      case OrderStatusDto.LiveUntriggered:
        return OrderEventStatusLabel.LiveUntriggered;
      case OrderStatusDto.Scheduled:
        return OrderEventStatusLabel.Scheduled;
      case OrderStatusDto.OCO_Untriggered:
        return OrderEventStatusLabel.OCOUntriggered;
      case OrderStatusDto.CancelledUntriggered:
        return OrderEventStatusLabel.CancelledUntriggered;
      case OrderStatusDto.Initiated:
        return OrderEventStatusLabel.Queued;
      case OrderStatusDto.ReplaceInitiated:
        return OrderEventStatusLabel.ReplaceInitiated;
      case OrderStatusDto.CancelInitiated:
        return OrderEventStatusLabel.CancelInitiated;
      case OrderStatusDto.CancelRejected:
        return OrderEventStatusLabel.CancelRejected;
      case OrderStatusDto.ReplaceRejected:
        return OrderEventStatusLabel.ReplaceRejected;
      case OrderStatusDto.Busted:
        return OrderEventStatusLabel.Busted;
      case OrderStatusDto.PreAllocated:
        return OrderEventStatusLabel.PreAllocated;
      case OrderStatusDto.Acknowledged:
        return OrderEventStatusLabel.Acknowledged;
      case OrderStatusDto.PartiallyCancelled:
        return OrderEventStatusLabel.PartiallyCancelled;
    }
  }

  get value(): OrderStatusDto {
    return this._value;
  }

  get label(): OrderEventStatusLabel {
    return this._label;
  }

  get isCompleted() {
    return this._value === OrderStatusDto.Filled;
  }

  get isCancelled() {
    return this._value === OrderStatusDto.Cancelled || this._value === OrderStatusDto.PartiallyCancelled;
  }

  get isPendingNew() {
    return this._value === OrderStatusDto.PendingNew;
  }
}

export class OrderType {
  private _value: OrderTypeDto;
  private _label: OrderTypeLabel;

  constructor(type: OrderTypeIntDto | OrderTypeDto) {
    this._value = isNumber(type) ? this.mapTypeNumberToEnum(type) : (type as OrderTypeDto);
    this._label = this.mapTypeEnumToLabel(this._value);
  }

  private mapTypeNumberToEnum(type: OrderTypeIntDto): OrderTypeDto {
    switch (type) {
      case OrderTypeIntDto.Market:
        return OrderTypeDto.Market;
      case OrderTypeIntDto.Limit:
        return OrderTypeDto.Limit;
      case OrderTypeIntDto.Stop_Market:
        return OrderTypeDto.StopMarket;
      case OrderTypeIntDto.Stop_Limit:
        return OrderTypeDto.StopLimit;
      case OrderTypeIntDto.Market_On_Close:
        return OrderTypeDto.MarketOnClose;
      default:
        return OrderTypeDto.Market;
    }
  }

  private mapTypeEnumToLabel(type: OrderTypeDto): OrderTypeLabel {
    switch (type) {
      case OrderTypeDto.Market:
        return OrderTypeLabel.Market;
      case OrderTypeDto.Limit:
        return OrderTypeLabel.Limit;
      case OrderTypeDto.StopMarket:
        return OrderTypeLabel.StopMarket;
      case OrderTypeDto.StopLimit:
        return OrderTypeLabel.StopLimit;
      case OrderTypeDto.MarketOnClose:
        return OrderTypeLabel.MarketOnClose;
      default:
        return OrderTypeLabel.Market;
    }
  }

  get value(): OrderTypeDto {
    return this._value;
  }

  get label(): OrderTypeLabel {
    return this._label;
  }

  get isMarket(): boolean {
    return this._value === OrderTypeDto.Market || this._value === OrderTypeDto.MarketOnClose;
  }

  get isLimit(): boolean {
    return this._value === OrderTypeDto.Limit || this._value === OrderTypeDto.StopLimit;
  }

  get isStopMarket(): boolean {
    return this._value === OrderTypeDto.StopMarket;
  }

  get isStopLimit(): boolean {
    return this._value === OrderTypeDto.StopLimit;
  }
}

export class OrderAction {
  private _value: OrderActionDto;
  private _label: OrderActionLabel;

  constructor(action?: OrderActionIntDto | OrderActionDto) {
    this._value = isNumber(action) ? this.mapActioNumberToEnum(action) : (action as OrderActionDto);
    this._label = this.findLabel();
  }

  private mapActioNumberToEnum(action: OrderActionIntDto): OrderActionDto {
    return action === OrderActionIntDto.Buy ? OrderActionDto.Buy : OrderActionDto.Sell;
  }

  private findLabel(): OrderActionLabel {
    return this._value === OrderActionDto.Buy ? OrderActionLabel.Buy : OrderActionLabel.Sell;
  }

  get value(): OrderActionDto {
    return this._value;
  }

  get label(): OrderActionLabel {
    return this._label;
  }

  get isBuy(): boolean {
    return this._value === OrderActionDto.Buy;
  }

  get isSell(): boolean {
    return this._value === OrderActionDto.Sell;
  }
}

export class OrderDuration {
  private _value: OrderDurationDto;
  private _label: OrderDurationLabel;

  constructor(duration: OrderDurationIntDto | OrderDurationDto) {
    this._value = isNumber(duration) ? this.mapDurationNumberToEnum(duration) : (duration as OrderDurationDto);
    this._label = this.mapDurationEnumToLabel(this._value);
  }

  private mapDurationNumberToEnum(duration: OrderDurationIntDto): OrderDurationDto {
    return duration === OrderDurationIntDto.Day ? OrderDurationDto.Day : OrderDurationDto.GTC;
  }

  private mapDurationEnumToLabel(duration: OrderDurationDto): OrderDurationLabel {
    switch (duration) {
      case OrderDurationDto.Day:
        return OrderDurationLabel.Day;
      case OrderDurationDto.GTC:
        return OrderDurationLabel.GTC;
    }
  }

  get value(): OrderDurationDto {
    return this._value;
  }

  get label(): OrderDurationLabel {
    return this._label;
  }
}

export interface OrderEvent {
  description?: string;
  fillPrice: number;
  fillQuantity: number;
  limit?: number;
  stop?: number;
  status: OrderEventStatus;
  symbol?: string;
  createdAt: string;
}

export interface Order {
  id: string;
  symbol: string;
  description: string;
  updatedAt: string;
  quantity: number;
  filledQuantity: number;
  isPartial: boolean;
  price: number;
  totalCost: number;
  type: OrderType;
  action: OrderAction;
  status: OrderStatus;
  duration: OrderDuration;
  commission: number;
  limit?: number;
  stop?: number;
}

export interface OrderDetails extends Order {
  events: OrderEvent[];
}

export interface SecurityOrderView {
  type?: OrderTypeDto;
  action?: OrderActionDto;
  duration?: OrderDurationDto;
  quantity?: number;
  price?: number;
  stop?: number;
  estimatedTotal?: number;
}
